"use client";

import { compressImageIfNeeded } from '@/lib/utils/imageCompression';
import { ImagePlus, Trash2 } from 'lucide-react';
import { toDatetimeLocalValue } from '@/lib/social-media/facebook-schedule-fields';

type ToastFn = (message: string, type: 'success' | 'error' | 'info') => void;

export type BlogSocialPostingLabels = {
  title?: string;
  subtitle?: string;
  enabledLabel?: string;
  captionPlaceholder?: string;
};

interface BlogSocialMediaPostingSectionProps {
  featuredImage: string;
  enabled: boolean;
  scheduledAt: string;
  publishedAt?: string;
  useFeaturedImage: boolean;
  caption: string;
  tagsStr: string;
  extraImageUrls: string[];
  showToast: ToastFn;
  onScheduledAtChange: (value: string) => void;
  onEnabledChange?: (enabled: boolean) => void;
  onUseFeaturedChange: (v: boolean) => void;
  onCaptionChange: (v: string) => void;
  onTagsStrChange: (v: string) => void;
  onExtraUrlsChange: (urls: string[]) => void;
  facebookBlogAutoPostId?: string;
  facebookBlogAutoPostError?: string;
  /** Optional copy overrides (e.g. blog list modal). */
  labels?: BlogSocialPostingLabels;
  /** Compact Facebook-style layout for the blog-list compose modal. */
  variant?: 'editor' | 'fbCard';
  mode?: 'scheduler' | 'manual';
}

export default function BlogSocialMediaPostingSection({
  featuredImage,
  enabled,
  scheduledAt,
  publishedAt,
  useFeaturedImage,
  caption,
  tagsStr,
  extraImageUrls,
  facebookBlogAutoPostId,
  facebookBlogAutoPostError,
  labels,
  showToast,
  onScheduledAtChange,
  onEnabledChange,
  onUseFeaturedChange,
  onCaptionChange,
  onTagsStrChange,
  onExtraUrlsChange,
  variant = 'editor',
  mode = 'scheduler',
}: BlogSocialMediaPostingSectionProps) {
  const isCard = variant === 'fbCard';
  const isScheduler = mode === 'scheduler';
  const hasSchedule = !!scheduledAt.trim();
  const scheduleLocked = isScheduler && !!(facebookBlogAutoPostId?.trim() || publishedAt?.trim());
  const minScheduleAt = toDatetimeLocalValue(new Date());
  const postedDate = publishedAt ? new Date(publishedAt) : null;
  const scheduledDate = hasSchedule ? new Date(scheduledAt) : null;
  const formatDateTime = (date: Date | null) =>
    date && !Number.isNaN(date.getTime())
      ? date.toLocaleString([], {
          year: 'numeric',
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      : '';
  const handleExtraUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files?.length) return;
    const allowed = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const next = [...extraImageUrls];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!allowed.includes(file.type)) {
          showToast('Only JPEG, PNG, GIF, and WebP are allowed for Facebook posting.', 'error');
          continue;
        }
        const compressed = await compressImageIfNeeded(file, showToast);
        const fd = new FormData();
        fd.append('file', compressed);
        const res = await fetch('/api/upload/blogs', { method: 'POST', body: fd });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error((err as { error?: string }).error || 'Upload failed');
        }
        const data = (await res.json()) as { fileUrl?: string };
        if (data.fileUrl) next.push(data.fileUrl);
      }
      onExtraUrlsChange(next);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Upload failed', 'error');
    } finally {
      e.target.value = '';
    }
  };

  const removeExtra = (url: string) => {
    onExtraUrlsChange(extraImageUrls.filter((u) => u !== url));
  };

  return (
    <div
      {...(isCard ? {} : { id: 'social-media-posting' })}
      className={
        isCard
          ? 'text-[15px] leading-snug text-[#050505]'
          : 'mt-6 pt-6 border-t border-sky-200/50'
      }
    >
      <h3
        className={
          isCard
            ? 'text-[15px] font-semibold text-[#050505] mb-0.5'
            : 'text-xl font-semibold text-gray-900 tracking-tight mb-1'
        }
      >
        {labels?.title ?? 'Social-media posting'}
      </h3>
      <p
        className={
          isCard
            ? 'text-[13px] text-[#65676b] mb-3'
            : 'text-sm text-gray-600 mb-4'
        }
      >
        {labels?.subtitle ??
          (isCard
            ? 'Link and hashtags are added automatically after your caption.'
            : 'Schedule a Facebook photo post for this blog. Caption format: your text, then "Read our blog" with the link, then tags as hashtags.')}
      </p>

      {isScheduler ? (
        <div className={isCard ? 'mb-3' : 'mb-4'}>
          <label
            className={
              isCard
                ? 'block text-[13px] font-semibold text-[#65676b] mb-1.5'
                : 'block text-sm font-semibold text-gray-800 mb-2'
            }
          >
            Publish Date & Time
          </label>
          <input
            type="datetime-local"
            value={scheduledAt}
            min={minScheduleAt}
            disabled={scheduleLocked}
            onChange={(e) => onScheduledAtChange(e.target.value)}
            className={
              isCard
                ? 'w-full border border-[#dddfe2] rounded-md px-3 py-2 text-[15px] bg-white focus:border-[#0866FF] focus:outline-none focus:ring-1 focus:ring-[#0866FF] disabled:bg-[#f0f2f5] disabled:text-[#65676b]'
                : 'w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm transition-all focus:border-sky-500 focus:outline-none bg-white hover:border-sky-300 disabled:bg-sky-50 disabled:text-gray-500'
            }
          />
        </div>
      ) : (
        <label className={`flex items-center gap-2 mb-3 cursor-pointer ${isCard ? '' : 'mb-4'}`}>
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => onEnabledChange?.(e.target.checked)}
            className={
              isCard
                ? 'h-4 w-4 rounded border-[#ced0d4] text-[#0866FF] focus:ring-[#0866FF]'
                : 'h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500'
            }
          />
          <span className={isCard ? 'text-[15px] font-semibold text-[#050505]' : 'text-sm font-semibold text-gray-800'}>
            {labels?.enabledLabel ?? 'Enable Facebook posting'}
          </span>
        </label>
      )}

      <div className={`${isCard ? 'space-y-3' : 'space-y-4'} ${!isScheduler && !enabled ? 'opacity-50 pointer-events-none' : ''}`}>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={useFeaturedImage}
            onChange={(e) => onUseFeaturedChange(e.target.checked)}
            className={
              isCard
                ? 'h-4 w-4 rounded border-[#ced0d4] text-[#0866FF] focus:ring-[#0866FF]'
                : 'h-4 w-4 rounded border-gray-300 text-sky-600 focus:ring-sky-500'
            }
          />
          <span className={isCard ? 'text-[15px] font-medium text-[#050505]' : 'text-sm font-medium text-gray-800'}>
            Include featured image
          </span>
        </label>

        {useFeaturedImage && featuredImage?.trim() && (
          <div
            className={
              isCard
                ? 'flex items-center gap-3 p-2 rounded-md border border-[#dddfe2] bg-[#f0f2f5]/80'
                : 'flex items-center gap-3 p-2 rounded-lg border border-sky-100 bg-sky-50/40'
            }
          >
            <div className="h-16 w-16 shrink-0 rounded border border-[#dddfe2] overflow-hidden bg-white">
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img src={featuredImage} alt="" className="h-full w-full object-cover" />
            </div>
            <span className={isCard ? 'text-[13px] text-[#65676b]' : 'text-xs text-gray-600 break-all'}>
              Used first in the Facebook photo post.
            </span>
          </div>
        )}

        <div>
          <label
            className={
              isCard
                ? 'block text-[13px] font-semibold text-[#65676b] mb-1.5'
                : 'block text-sm font-semibold text-gray-800 mb-2'
            }
          >
            Caption
          </label>
          <textarea
            value={caption}
            onChange={(e) => onCaptionChange(e.target.value)}
            rows={isCard ? 3 : 4}
            className={
              isCard
                ? 'w-full border border-[#dddfe2] rounded-md px-3 py-2 text-[15px] bg-white focus:border-[#0866FF] focus:outline-none focus:ring-1 focus:ring-[#0866FF] resize-none placeholder:text-[#65676b]'
                : 'w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm transition-all focus:border-sky-500 focus:outline-none resize-none bg-white hover:border-sky-300'
            }
            placeholder={
              labels?.captionPlaceholder ??
              (isCard
                ? 'Optional intro (link & hashtags added automatically)'
                : 'Intro text for the Facebook post (optional). “Read our blog - {link}” and hashtags are added automatically.')
            }
          />
        </div>

        <div>
          <label
            className={
              isCard
                ? 'block text-[13px] font-semibold text-[#65676b] mb-1.5'
                : 'block text-sm font-semibold text-gray-800 mb-2'
            }
          >
            Tags
          </label>
          <input
            type="text"
            value={tagsStr}
            onChange={(e) => onTagsStrChange(e.target.value)}
            className={
              isCard
                ? 'w-full border border-[#dddfe2] rounded-md px-3 py-2 text-[15px] bg-white focus:border-[#0866FF] focus:outline-none focus:ring-1 focus:ring-[#0866FF] placeholder:text-[#65676b]'
                : 'w-full border-2 border-sky-200 rounded-lg px-4 py-2.5 text-sm transition-all focus:border-sky-500 focus:outline-none bg-white hover:border-sky-300'
            }
            placeholder={isCard ? 'Comma-separated hashtags' : 'Comma-separated (e.g. social-media, MMBIndia, AI)'}
          />
        </div>

        <div>
          <span
            className={
              isCard
                ? 'block text-[13px] font-semibold text-[#65676b] mb-1'
                : 'block text-sm font-semibold text-gray-800 mb-2'
            }
          >
            Extra images
          </span>
          {!isCard ? (
            <p className="text-xs text-gray-500 mb-2">
              Upload one or more images in addition to (or instead of) the featured image.
            </p>
          ) : (
            <p className="text-[13px] text-[#65676b] mb-2">Optional — in addition to or instead of the featured image.</p>
          )}
          <label
            className={
              isCard
                ? 'inline-flex items-center gap-2 cursor-pointer text-[#0866FF] hover:underline text-[15px] font-semibold'
                : 'inline-flex items-center gap-2 cursor-pointer text-sky-600 hover:text-sky-700 text-sm font-semibold'
            }
          >
            <ImagePlus className="h-4 w-4" />
            <span>Add images</span>
            <input type="file" accept="image/jpeg,image/png,image/gif,image/webp" multiple className="hidden" onChange={handleExtraUpload} />
          </label>
          {extraImageUrls.length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2">
              {extraImageUrls.map((url) => (
                <div
                  key={url}
                  className={
                    isCard
                      ? 'relative h-20 w-20 rounded-md border border-[#dddfe2] overflow-hidden bg-[#f0f2f5]'
                      : 'relative h-20 w-20 rounded border border-gray-200 overflow-hidden bg-gray-50'
                  }
                >
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <button
                    type="button"
                    onClick={() => removeExtra(url)}
                    className="absolute top-0.5 right-0.5 p-1 rounded bg-red-600 text-white opacity-90 hover:bg-red-700"
                    title="Remove"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {(facebookBlogAutoPostId || facebookBlogAutoPostError) && (
        <div
          className={
            isCard
              ? 'mt-3 rounded-md border border-[#dddfe2] bg-[#f0f2f5] px-3 py-2 text-[13px] space-y-1'
              : 'mt-4 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-xs space-y-1'
          }
        >
          {facebookBlogAutoPostId ? (
            <p className="text-green-800">
              Published to Facebook
              {formatDateTime(postedDate) ? ` at ${formatDateTime(postedDate)}` : ''} (id:{' '}
              <span className="font-mono break-all">{facebookBlogAutoPostId}</span>)
            </p>
          ) : null}
          {facebookBlogAutoPostError ? <p className="text-red-700">{facebookBlogAutoPostError}</p> : null}
        </div>
      )}

      {isScheduler && !facebookBlogAutoPostId && !facebookBlogAutoPostError && enabled && hasSchedule ? (
        <div
          className={
            isCard
              ? 'mt-3 rounded-md border border-[#dddfe2] bg-[#f0f2f5] px-3 py-2 text-[13px] text-[#65676b]'
              : 'mt-4 rounded-lg border border-sky-100 bg-sky-50/60 px-3 py-2 text-xs text-gray-600'
          }
        >
          Scheduled for {formatDateTime(scheduledDate)}
        </div>
      ) : null}
    </div>
  );
}
